import { useConfiguration } from "@/app/hooks/useConfiguration";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import React, { useState, useEffect, useRef } from "react";
import ImageToUrlGeneration from "../ImageToUrlGeneration";
import SchemaconfirmationModal from "./SchemaConfermationModal";
import { ORG_APIS } from "@/api_framework/api_config";
import axios from "axios";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { HiArrowRight } from "react-icons/hi";

interface SlugContent {
  [key: string]: any;
}

type TCmsFieldChange = { field: string; from: any; to: any };
const collectChangedFields = (
  before: any,
  after: any,
  path: string[] = [],
  changes: TCmsFieldChange[] = [],
): TCmsFieldChange[] => {
  const bothCollections =
    before !== null &&
    after !== null &&
    typeof before === "object" &&
    typeof after === "object" &&
    Array.isArray(before) === Array.isArray(after);

  if (bothCollections) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    keys.forEach((key) => collectChangedFields(before[key], after[key], [...path, key], changes));
    return changes;
  }

  if (JSON.stringify(before) !== JSON.stringify(after)) {
    changes.push({ field: path.join("."), from: before, to: after });
  }
  return changes;
};

interface SlugManagementProps {
  site?: "doggyworld";
}

const SlugManagement: React.FC<SlugManagementProps> = ({ site }) => {
  const [pageSlug, setPageSlug] = useState("");
  const [pageType, setPageType] = useState("");
  const [canonicalTag, setCanonicalTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentData, setContentData] = useState<SlugContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContentData, setEditedContentData] = useState<SlugContent | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>({});
  const [initialCollapseSet, setInitialCollapseSet] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [imageUploadTarget, setImageUploadTarget] = useState<{
    path: string[];
    onChange: (url: string) => void;
  } | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [justUpdatedFieldPath, setJustUpdatedFieldPath] = useState<string | null>(null);
  const [imageUploaderResetCount, setImageUploaderResetCount] = useState(0);
  const [showAddFaqModal, setShowAddFaqModal] = useState(false);
  const [newFaqItem, setNewFaqItem] = useState({ title: "", content: "" });
  const [faqPath, setFaqPath] = useState<string[]>([]);
  const [showAddClinicModal, setShowAddClinicModal] = useState(false);
  const [newClinicName, setNewClinicName] = useState("");
  const [clinicPath, setClinicPath] = useState<string[]>([]);
  const [showAddPackagePointModal, setShowAddPackagePointModal] = useState(false);
  const [newPackagePoint, setNewPackagePoint] = useState("");
  const [packagePointPath, setPackagePointPath] = useState<string[]>([]);
  const [showAddWhyChooseCardModal, setShowAddWhyChooseCardModal] = useState(false);
  const [newWhyChooseCard, setNewWhyChooseCard] = useState({ heading: "", image: "" });
  const [whyChooseCardPath, setWhyChooseCardPath] = useState<string[]>([]);

  // ── Schema states ──
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [schemaInput, setSchemaInput] = useState("");
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [schemaValid, setSchemaValid] = useState(false);
  const [schemaPath, setSchemaPath] = useState<string[]>([]);
  const [showSchemaDeleteConfirm, setShowSchemaDeleteConfirm] = useState(false);
  const [pendingDeleteSchemaPath, setPendingDeleteSchemaPath] = useState<string[]>([]);
  const [resolvedSlug, setResolvedSlug] = useState("");

  const configuration = useConfiguration();
  const { execute: logClickAPI } = useClickLogAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const vmsUserEmail = userLogInfo ? userLogInfo.toLowerCase() : "unknown_user";
  const cmsOpenedAtRef = useRef(Date.now());

  useEffect(() => {
    if (!isEditing || initialCollapseSet) {
      if (!isEditing) setInitialCollapseSet(false);
      return;
    }

    // Astro pages nest their sections under page_content.body.main. Doggy World pages
    // (and anything else simpler) put sections directly under page_content. Collapse
    // whichever one of these is actually the content root, so neither site is stuck
    // fully expanded just because it doesn't match the other's shape.
    const astroMain = editedContentData?.data?.page_content?.body?.main;
    const isObject = (value: unknown): value is Record<string, unknown> =>
      !!value && typeof value === "object" && !Array.isArray(value);

    let rootPrefix: string | null = null;
    let rootKeys: string[] = [];

    if (isObject(astroMain)) {
      rootPrefix = "page_content.body.main";
      rootKeys = Object.keys(astroMain);
    } else if (isObject(editedContentData?.data?.page_content)) {
      rootPrefix = "page_content";
      rootKeys = Object.keys(editedContentData.data.page_content);
    }

    if (rootPrefix && rootKeys.length > 0) {
      const initialCollapsed: Record<string, boolean> = {};
      rootKeys.forEach((key) => {
        initialCollapsed[`${rootPrefix}.${key}`] = true;
      });
      setCollapsedState(initialCollapsed);
      setInitialCollapseSet(true);
    }
  }, [isEditing, editedContentData, initialCollapseSet]);

  const SLUG_RESPONSE_PATHS = [
    ["page_slug"],
    ["data", "page_slug"],
    ["data", "page_content", "page_slug"],
    ["data", "page_content", "body", "page_slug"],
  ];

  const resolveSlugFromResponse = (response: any): string => {
    for (const path of SLUG_RESPONSE_PATHS) {
      const value = getNestedValue(response, path);
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };

  const slugFromCanonicalTag = (tag: string): string => {
    const raw = tag.trim();
    if (!raw) return "";
    try {
      const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
      const parts = url.pathname.split("/").filter(Boolean);
      const lastSegment = parts.length ? decodeURIComponent(parts[parts.length - 1]) : "";
      // Doggy World pages are served as physical .php files (e.g. ultrasound.php) —
      // the backend's page_slug for them never includes the extension.
      return lastSegment.replace(/\.php$/i, "");
    } catch {
      return "";
    }
  };

  const CANONICAL_RESPONSE_PATHS = [
    ["data", "page_content", "body", "main", "meta", "canonicalTag"],
    ["data", "page_content", "body", "meta", "canonicalTag"],
    ["data", "page_content", "meta", "canonicalTag"],
  ];

  // The response carries the real live URL, so the preview link never has to be guessed.
  const resolveCanonicalFromResponse = (response: any): string => {
    for (const path of CANONICAL_RESPONSE_PATHS) {
      const value = getNestedValue(response, path);
      if (typeof value === "string" && value.trim()) {
        return value.trim().replace(/^http:\/\//i, "https://");
      }
    }
    return "";
  };

  // City landing pages live at /{city}/lp/{slug} — the /lp/ segment is not optional.
  const buildFallbackPageUrl = (slug: string, type: string): string =>
    site === "doggyworld"
      ? `https://doggyworld.in/${slug}.php`
      : `https://vetic.in/${type.trim() ? `${type.trim()}/lp/` : ""}${slug}`;

  const previewUrl =
    canonicalTag.trim() ||
    resolveCanonicalFromResponse(contentData) ||
    buildFallbackPageUrl(pageSlug.trim(), pageType.trim());

  // Doggy World page_types we've confirmed actually exist in doggyworldpages (seen via
  // the Django admin: "service" — ultrasound, vaccination; "clinic" — cat/dog/pet-clinic
  // pages). Slugs can collide with an unrelated Astro page of the same name (e.g. both
  // sites have a "cat-clinic-delhi"), and the backend has no site/brand column to tell
  // them apart — so when a plain page_slug search lands on the wrong (Astro) record, we
  // retry with each of these until the response's own canonicalTag confirms it's really
  // a doggyworld.in page.
  const DOGGY_WORLD_PAGE_TYPES = ["clinic", "service"];

  const isDoggyWorldResult = (data: any): boolean =>
    resolveCanonicalFromResponse(data).toLowerCase().includes("doggyworld.in");

  // ── Content Search funcality ──
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setContentData(null);
    setIsEditing(false);
    setEditedContentData(null);
    setSuccessMessage(null);
    setSaveError(null);
    setResolvedSlug("");
    const trimmedCanonicalTag = canonicalTag.trim();
    const trimmedPageSlug = pageSlug.trim();
    const trimmedPageType = pageType.trim();
    try {
      const doggyWorldSlug = site === "doggyworld" ? slugFromCanonicalTag(trimmedCanonicalTag) : "";

      const buildParams = (pageTypeOverride?: string): Record<string, string> => {
        if (trimmedCanonicalTag) {
          // The backend's canonical_tag lookup only recognises vetic.in URLs — it 400s on
          // doggyworld.in. For Doggy World, pull the slug out of the pasted URL ourselves
          // and search by page_slug instead, which works for both sites.
          if (site === "doggyworld") {
            const params: Record<string, string> = { page_slug: doggyWorldSlug };
            if (pageTypeOverride) params.page_type = pageTypeOverride;
            return params;
          }
          return { canonical_tag: trimmedCanonicalTag };
        }
        const params: Record<string, string> = { page_slug: trimmedPageSlug };
        if (trimmedPageType) params.page_type = trimmedPageType;
        return params;
      };

      let data: any = (await axios.get(ORG_APIS?.SLUG_SEARCH?.baseURL ?? "", { params: buildParams() }))
        .data;

      // Same slug exists on both sites and the backend returned the Astro one — retry
      // with each known Doggy World page_type until we land on the right record.
      if (site === "doggyworld" && doggyWorldSlug && data && !isDoggyWorldResult(data)) {
        for (const candidateType of DOGGY_WORLD_PAGE_TYPES) {
          try {
            const retryRes = await axios.get(ORG_APIS?.SLUG_SEARCH?.baseURL ?? "", {
              params: buildParams(candidateType),
            });
            if (retryRes.data && isDoggyWorldResult(retryRes.data)) {
              data = retryRes.data;
              break;
            }
          } catch {
            // try the next page_type
          }
        }
      }

      if (!data || Object.keys(data).length === 0) {
        setError("No content found for the given search parameters.");
      } else if (site === "doggyworld" && doggyWorldSlug && !isDoggyWorldResult(data)) {
        setError(
          "Found a page with this slug, but it belongs to the Vetic website, not Doggy World. Please check the URL.",
        );
      } else {
        setContentData(data);
        setResolvedSlug(resolveSlugFromResponse(data) || trimmedPageSlug || doggyWorldSlug);
      }
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response) {
        setError("Please check the canonical tag for relevant page.");
      } else {
        setError(err?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditedContentData(JSON.parse(JSON.stringify(contentData)));
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContentData(null);
  };

  // ── save changes funcality ──
  const handleSaveChanges = async () => {
    if (!editedContentData) return;
    setSaveLoading(true);
    setSaveError(null);

    // Resolved outside the try so the catch block can name the slug in its error message
    const slug =
      resolvedSlug ||
      resolveSlugFromResponse(contentData) ||
      resolveSlugFromResponse(editedContentData) ||
      pageSlug.trim() ||
      slugFromCanonicalTag(canonicalTag);

    const changedFields = collectChangedFields(contentData?.data, editedContentData.data);

    const logUpdate = (status: string) =>
      logClickAPI({
        event_type: "CMS_CONTENT_UPDATE",
        scr_id: "Website_CMS",
        vms_user_id: vmsUserEmail,
        opened_at: cmsOpenedAtRef.current,
        timestamp: Date.now(),
        cms_page_slug: slug,
        cms_status: status,
        cms_changed_field_count: changedFields.length,
        cms_changed_fields: changedFields,
      });

    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);

      if (!slug) {
        throw new Error("Unable to determine page slug. Please search again.");
      }

      const res = await axios.put(`${ORG_APIS?.SLUG_UPDATE?.baseURL}${slug}`, editedContentData.data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${accessToken}`,
        },
      });
      const updatedData = res.data;
      logUpdate("Data updated successfully");
      setContentData(updatedData);
      setIsEditing(false);
      setEditedContentData(null);
      setSuccessMessage(updatedData.message || "Page Content Updated");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        setSuccessMessage(null);
        setContentData(null);
        setPageSlug("");
        setPageType("");
        setCanonicalTag("");
        setResolvedSlug("");
        setCollapsedState({});
      }, 100000);
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data as any;
        setSaveError(
          errorData?.message ||
            errorData?.detail ||
            `Failed to save changes (${err.response.status} ${err.response.statusText}) for slug "${slug}".`,
        );
      } else {
        setSaveError(err?.message || "Something went wrong while saving.");
      }
      logUpdate("Data update failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const isDirectMetaPath = (path: string[]): boolean => {
    if (path.length === 0) return false;
    if (path[path.length - 1] !== "meta") return false;
    return !path.some((segment) => /^\d+$/.test(segment));
  };

  // ── Schema handlers ──
  const handleSchemaInputChange = (value: string) => {
    setSchemaInput(value);
    setSchemaError(null);
    setSchemaValid(false);
    if (!value.trim()) return;
    try {
      JSON.parse(value);
      setSchemaValid(true);
    } catch (e: any) {
      setSchemaError(`Invalid JSON: ${e.message}`);
    }
  };

  // Opens the modal. If schema already exists it pre-fills the textarea.
  const handleOpenSchemaModal = (metaPath: string[]) => {
    setSchemaPath(metaPath);
    const existing = getNestedValue(editedContentData?.data, [...metaPath, "schemaData"]);
    setSchemaInput(existing ? JSON.stringify(existing, null, 2) : "");
    setSchemaError(null);
    setSchemaValid(!!existing);
    setShowSchemaModal(true);
  };

  // Deletes schemaData key from the meta object
  const handleDeleteSchema = (metaPath: string[]) => {
    if (!editedContentData) return;
    const metaObj = getNestedValue(editedContentData.data, metaPath);
    if (!metaObj || typeof metaObj !== "object") return;
    const { schemaData: _removed, ...rest } = metaObj;
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, metaPath, rest),
    });
  };

  const handleDeleteSchemaClick = (metaPath: string[]) => {
    setPendingDeleteSchemaPath(metaPath);
    setShowSchemaDeleteConfirm(true);
  };

  const handleSaveSchema = () => {
    if (!editedContentData || !schemaValid) return;
    try {
      const parsed = JSON.parse(schemaInput);
      setEditedContentData({
        ...editedContentData,
        data: setNestedValue(editedContentData.data, [...schemaPath, "schemaData"], parsed),
      });
      closeSchemaModal();
    } catch {
      setSchemaError("Failed to parse JSON.");
    }
  };

  const closeSchemaModal = () => {
    setShowSchemaModal(false);
    setSchemaInput("");
    setSchemaError(null);
    setSchemaValid(false);
    setSchemaPath([]);
  };

  // ── ALT tag handler ────────────────────────────────────────────────────────

  const handleAddAltTag = (parentPath: string[], imageKey: string, altKey: string) => {
    if (!editedContentData) return;
    const parentObj = getNestedValue(editedContentData.data, parentPath);
    if (!parentObj || typeof parentObj !== "object" || Array.isArray(parentObj)) return;
    const newObj: Record<string, any> = {};
    Object.keys(parentObj).forEach((k) => {
      newObj[k] = parentObj[k];
      if (k === imageKey) newObj[altKey] = "";
    });
    if (!(altKey in newObj)) newObj[altKey] = "";
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, parentPath, newObj),
    });
  };

  // ── Image field helpers ──
  const isImageValue = (value: any): boolean =>
    typeof value === "string" && value.startsWith("https://vetic-img.s3.ap-south-1.amazonaws.com");
  const isEmptyImageField = (key: string, value: any): boolean =>
    typeof value === "string" && value.trim() === "" && /(image|img|icon|photo|banner|thumbnail)/i.test(key);

  // ── renderEditableJson ──
  const renderEditableJson = (
    data: any,
    onChange: (path: string[], value: any) => void,
    path: string[] = [],
    collapsedState: Record<string, boolean>,
    setCollapsedState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  ) => {
    const pathKey = path.join(".");
    const currentKey = path.length ? path[path.length - 1] : "";
    const isCollapsed = collapsedState[pathKey] || false;

    if (Array.isArray(data) || (typeof data === "object" && data !== null)) {
      return (
        <div className="space-y-2">
          {path.length > 0 && (
            <div
              className="flex items-center gap-2 py-2.5 px-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-[#0467ff] hover:shadow-sm transition-all"
              onClick={() => setCollapsedState((prev) => ({ ...prev, [pathKey]: !isCollapsed }))}
            >
              <svg
                className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isCollapsed ? "" : "rotate-90"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
              <span className="font-semibold text-sm text-gray-900 capitalize">
                {currentKey.replace(/_/g, " ")}
              </span>
            </div>
          )}
          {!isCollapsed && (
            <div className="pl-4 border-l-2 border-[#EAECF0]">
              {Array.isArray(data) ? (
                <div>
                  {data.map((item, idx) => (
                    <div
                      key={idx}
                      className="mb-3 border border-gray-200 rounded-xl p-4 bg-white shadow-sm"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-[#EEF4FF] text-[#0467ff] text-[11px] font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="text-xs text-gray-500 font-semibold">Item {idx + 1}</span>
                        </div>
                        {isFaqPath(path) && (
                          <button
                            type="button"
                            className="px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg border border-red-200 hover:bg-red-100 hover:border-red-300 text-xs font-semibold flex items-center gap-1 transition-colors"
                            onClick={() => handleDeleteFaqItem(path, idx)}
                            title="Delete FAQ Item"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </button>
                        )}
                        {isClinicLocationPath(path) && (
                          <button
                            type="button"
                            className="px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg border border-red-200 hover:bg-red-100 hover:border-red-300 text-xs font-semibold flex items-center gap-1 transition-colors"
                            onClick={() => handleDeleteClinicLocation(path, idx)}
                            title="Delete Clinic Location"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </button>
                        )}
                        {isWhyChooseCardsPath(path) && (
                          <button
                            type="button"
                            className="px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg border border-red-200 hover:bg-red-100 hover:border-red-300 text-xs font-semibold flex items-center gap-1 transition-colors"
                            onClick={() => handleDeleteWhyChooseCard(path, idx)}
                            title="Delete Card"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </button>
                        )}
                        {isPackageCardsDataPath(path) && (
                          <button
                            type="button"
                            className="px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg border border-red-200 hover:bg-red-100 hover:border-red-300 text-xs font-semibold flex items-center gap-1 transition-colors"
                            onClick={() => handleDeletePackagePoint(path, idx)}
                            title="Delete Point"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </button>
                        )}
                      </div>
                      {renderEditableJson(item, onChange, [...path, String(idx)], collapsedState, setCollapsedState)}
                    </div>
                  ))}
                  {isFaqPath(path) && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="px-3.5 py-2 bg-[#ECFDF3] text-[#17764F] rounded-lg border border-[#ABEFC6] hover:bg-[#D7F8E4] hover:border-[#75E0A7] text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
                        onClick={() => handleAddFaqItem(path)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add FAQ Item
                      </button>
                    </div>
                  )}
                  {isClinicLocationPath(path) && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="px-3.5 py-2 bg-[#EEF4FF] text-[#0467ff] rounded-lg border border-[#C2D9FF] hover:bg-[#DCE9FF] hover:border-[#9DC2FF] text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
                        onClick={() => handleAddClinicLocation(path)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Clinic Location
                      </button>
                    </div>
                  )}
                  {isPackageCardsDataPath(path) && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="px-3.5 py-2 bg-[#ECFDF3] text-[#17764F] rounded-lg border border-[#ABEFC6] hover:bg-[#D7F8E4] hover:border-[#75E0A7] text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
                        onClick={() => handleAddPackagePoint(path)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Point
                      </button>
                    </div>
                  )}
                  {isWhyChooseCardsPath(path) && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="px-3.5 py-2 bg-[#ECFDF3] text-[#17764F] rounded-lg border border-[#ABEFC6] hover:bg-[#D7F8E4] hover:border-[#75E0A7] text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
                        onClick={() => handleAddWhyChooseCard(path)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Card
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* ─────────────────────────────────────────────────────────────
                      SCHEMA BLOCK — rendered exactly ONCE when this path IS meta
                      Uses isDirectMetaPath so it never repeats in nested children
                  ───────────────────────────────────────────────────────────── */}
                  {isDirectMetaPath(path) &&
                    (() => {
                      const hasSchema = "schemaData" in data;
                      return (
                        <div className="mb-4 p-3 rounded-lg border bg-purple-50 border-purple-200">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            {/* Left: label + status badge */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <svg
                                className="w-4 h-4 text-purple-600 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                                />
                              </svg>
                              <span className="text-sm font-semibold text-purple-800">Schema Data (JSON-LD)</span>

                              {hasSchema ? (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold border border-green-200">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2.5}
                                      d="M5 13l4 4L19 7"
                                      stroke="#16a34a"
                                    />
                                  </svg>
                                  Schema Added
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium border border-gray-200">
                                  No Schema
                                </span>
                              )}
                            </div>

                            {/* Right: action buttons */}
                            <div className="flex items-center gap-2">
                              {/* Add / Edit button */}
                              <button
                                type="button"
                                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 shadow-sm flex items-center gap-1.5 transition-colors"
                                onClick={() => handleOpenSchemaModal(path)}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {hasSchema ? (
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  ) : (
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 4v16m8-8H4"
                                    />
                                  )}
                                </svg>
                                {hasSchema ? "Edit Schema" : "Add Schema"}
                              </button>

                              {/* Delete button — only when schema exists */}
                              {hasSchema && (
                                <button
                                  type="button"
                                  className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 hover:border-red-300 border border-red-200 flex items-center gap-1.5 transition-colors"
                                  onClick={() => handleDeleteSchemaClick(path)}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  Delete Schema
                                </button>
                              )}
                              {/* ── Schema Delete Confirmation Modal ── */}
                              <SchemaconfirmationModal
                                open={showSchemaDeleteConfirm}
                                setOpen={setShowSchemaDeleteConfirm}
                                onConfirm={() => {
                                  handleDeleteSchema(pendingDeleteSchemaPath);
                                  setShowSchemaDeleteConfirm(false);
                                  setPendingDeleteSchemaPath([]);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  {/* ── All regular fields — skip schemaData (handled above) ── */}
                  {Object.entries(data).map(([key, value]) => {
                    // schemaData is hidden from normal field rendering — it has its own block above
                    if (isDirectMetaPath(path) && key === "schemaData") return null;

                    return (
                      <div key={key} className="mb-3">
                        {typeof value === "object" && value !== null ? (
                          renderEditableJson(value, onChange, [...path, key], collapsedState, setCollapsedState)
                        ) : (
                          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex-1 relative">
                              <label className="block text-xs font-semibold text-gray-700 mb-1.5 capitalize tracking-wide">
                                {key.replace(/_/g, " ")}
                              </label>
                              {["pageCategory", "page_type", "page_slug", "status", "message"].includes(key) ? (
                                <input
                                  type="text"
                                  className="w-full rounded-lg border border-gray-200 text-sm p-2 bg-gray-50 text-gray-500 cursor-not-allowed"
                                  value={value === null || value === undefined ? "" : String(value)}
                                  readOnly
                                />
                              ) : typeof value === "string" && value.length > 60 ? (
                                <textarea
                                  className="w-full rounded-lg border border-gray-300 text-sm p-2.5 transition-colors focus:outline-none focus:border-[#0467ff] focus:ring-1 focus:ring-[#84CAFF]"
                                  value={value}
                                  onChange={(e) => onChange([...path, key], e.target.value)}
                                  rows={3}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className="w-full rounded-lg border border-gray-300 text-sm p-2.5 transition-colors focus:outline-none focus:border-[#0467ff] focus:ring-1 focus:ring-[#84CAFF]"
                                  value={value === null || value === undefined ? "" : String(value)}
                                  onChange={(e) => onChange([...path, key], e.target.value)}
                                />
                              )}
                              {justUpdatedFieldPath === [...path, key].join(".") && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 animate-ping-once">
                                  <svg
                                    className="w-5 h-5 text-green-500"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      fill="#22c55e"
                                      opacity="0.2"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                      stroke="#22c55e"
                                      strokeWidth="2"
                                    />
                                  </svg>
                                </span>
                              )}
                            </div>
                            {(isImageValue(value) || isEmptyImageField(key, value)) && (
                              <button
                                type="button"
                                className="ml-2 px-2 py-3 bg-[#EEF4FF] text-[#0467ff] rounded-lg border border-[#C2D9FF] hover:bg-[#DCE9FF] hover:border-[#9DC2FF] text-xs sm:text-sm font-semibold shadow-sm transition-colors"
                                onClick={() =>
                                  setImageUploadTarget({
                                    path: [...path, key],
                                    onChange: (url: string) => onChange([...path, key], url),
                                  })
                                }
                              >
                                Image To Url
                              </button>
                            )}
                            {typeof value === "string" &&
                              value.startsWith("https://vetic-img.s3.ap-south-1.amazonaws.com") &&
                              (() => {
                                const IMG_PREFIX = "https://vetic-img.s3.ap-south-1.amazonaws.com";
                                // All image fields in the current object
                                const imageKeys = Object.keys(data).filter(
                                  (k) => typeof data[k] === "string" && (data[k] as string).startsWith(IMG_PREFIX),
                                );
                                // Single image → generic "image_alt"; multiple images → "<key>_alt"
                                // (e.g. desktop_image_alt / mobile_image_alt)
                                const altKey = imageKeys.length > 1 ? `${key}_alt` : "image_alt";
                                const hasAlt = altKey in data && data[altKey] !== null && data[altKey] !== undefined;
                                return (
                                  <button
                                    type="button"
                                    disabled={hasAlt}
                                    title={hasAlt ? "ALT tag already added" : "Add an ALT tag for this image"}
                                    className={`ml-2 px-2 py-3 rounded-lg border text-xs sm:text-sm font-semibold transition-colors ${
                                      hasAlt
                                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                        : "bg-[#ECFDF3] text-[#17764F] border-[#ABEFC6] hover:bg-[#D7F8E4] hover:border-[#75E0A7] shadow-sm"
                                    }`}
                                    onClick={() => handleAddAltTag(path, key, altKey)}
                                  >
                                    {hasAlt ? "ALT Tag Added" : "Add ALT Tag"}
                                  </button>
                                );
                              })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 text-sm p-2.5 transition-colors focus:outline-none focus:border-[#0467ff] focus:ring-1 focus:ring-[#84CAFF]"
              value={data === null || data === undefined ? "" : String(data)}
              onChange={(e) => onChange(path, e.target.value)}
            />
            {justUpdatedFieldPath === path.join(".") && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 animate-ping-once">
                <svg
                  className="w-5 h-5 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="#22c55e" opacity="0.2" />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                    stroke="#22c55e"
                    strokeWidth="2"
                  />
                </svg>
              </span>
            )}
          </div>
          {typeof data === "string" && data.startsWith("https://vetic-img.s3.ap-south-1.amazonaws.com") && (
            <button
              type="button"
              className="ml-2 px-2 py-3 bg-[#EEF4FF] text-[#0467ff] rounded-lg border border-[#C2D9FF] hover:bg-[#DCE9FF] hover:border-[#9DC2FF] text-xs sm:text-sm font-semibold shadow-sm transition-colors"
              onClick={() =>
                setImageUploadTarget({
                  path,
                  onChange: (url: string) => onChange(path, url),
                })
              }
            >
              Image To Url
            </button>
          )}
          {typeof data === "string" &&
            data.startsWith("https://vetic-img.s3.ap-south-1.amazonaws.com") &&
            (() => {
              const IMG_PREFIX = "https://vetic-img.s3.ap-south-1.amazonaws.com";
              const parentPath = path.slice(0, -1);
              const imageKey = path[path.length - 1];
              const parentObj = getNestedValue(editedContentData?.data, parentPath);
              // Alt tag can only be attached when the parent is a plain object
              if (!parentObj || typeof parentObj !== "object" || Array.isArray(parentObj)) {
                return null;
              }
              const imageKeys = Object.keys(parentObj).filter(
                (k) => typeof parentObj[k] === "string" && (parentObj[k] as string).startsWith(IMG_PREFIX),
              );
              // Single image → generic "image_alt"; multiple images → "<key>_alt"
              const altKey = imageKeys.length > 1 ? `${imageKey}_alt` : "image_alt";
              const hasAlt = altKey in parentObj && parentObj[altKey] !== null && parentObj[altKey] !== undefined;
              return (
                <button
                  type="button"
                  disabled={hasAlt}
                  title={hasAlt ? "ALT tag already added" : "Add an ALT tag for this image"}
                  className={`ml-2 px-2 py-3 rounded-lg border text-xs sm:text-sm font-semibold transition-colors ${
                    hasAlt
                      ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      : "bg-[#ECFDF3] text-[#17764F] border-[#ABEFC6] hover:bg-[#D7F8E4] hover:border-[#75E0A7] shadow-sm"
                  }`}
                  onClick={() => handleAddAltTag(parentPath, imageKey, altKey)}
                >
                  {hasAlt ? "ALT Tag Added" : "Add ALT Tag"}
                </button>
              );
            })()}
        </div>
      );
    }
  };

  // ── Nested value helpers ───────────────────────────────────────────────────
  function setNestedValue(obj: any, path: string[], value: any): any {
    if (path.length === 0) return value;
    const [first, ...rest] = path;
    if (Array.isArray(obj)) {
      const idx = Number(first);
      return obj.map((item, i) => (i === idx ? setNestedValue(item, rest, value) : item));
    } else {
      return {
        ...obj,
        [first]: rest.length ? setNestedValue(obj[first], rest, value) : value,
      };
    }
  }

  function getNestedValue(obj: any, path: string[]): any {
    return path.reduce((current, key) => current?.[key], obj);
  }

  // ── Path helpers ───────────────────────────────────────────────────────────
  const isFaqPath = (path: string[]): boolean => {
    return path.some(
      (segment) =>
        typeof segment === "string" &&
        (segment.toLowerCase().includes("faq") || segment.toLowerCase().includes("frequently")),
    );
  };

  const isClinicLocationPath = (path: string[]): boolean => {
    return path.some((segment) => {
      if (typeof segment !== "string") return false;
      const lowerSegment = segment.toLowerCase();
      return (
        lowerSegment.includes("cliniclocation") ||
        lowerSegment.includes("clinic_location") ||
        lowerSegment.includes("clinicloaction") ||
        lowerSegment.includes("location") ||
        lowerSegment === "clinicloaction" ||
        lowerSegment === "cliniclocation"
      );
    });
  };

  const isPackageCardsDataPath = (path: string[]): boolean => {
    if (path.length === 0) return false;
    return path[path.length - 1].toLowerCase() === "packagecardsdata";
  };

  // Matches the "cards" array inside whyChooseSection (whyChooseSection / why_choose_section / whychoose)
  const isWhyChooseCardsPath = (path: string[]): boolean => {
    if (path.length < 2) return false;
    if (path[path.length - 1].toLowerCase() !== "cards") return false;
    return path.some(
      (segment) => typeof segment === "string" && segment.toLowerCase().replace(/[_-]/g, "").includes("whychoose"),
    );
  };

  // ── FAQ handlers ───────────────────────────────────────────────────────────
  const handleAddFaqItem = (path: string[]) => {
    setFaqPath(path);
    setNewFaqItem({ title: "", content: "" });
    setShowAddFaqModal(true);
  };

  const handleSaveFaqItem = () => {
    if (!editedContentData || !newFaqItem.title.trim() || !newFaqItem.content.trim()) return;
    const currentArray = getNestedValue(editedContentData.data, faqPath) || [];
    const updatedArray = [...currentArray, { title: newFaqItem.title, content: newFaqItem.content }];
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, faqPath, updatedArray),
    });
    setShowAddFaqModal(false);
    setNewFaqItem({ title: "", content: "" });
    setFaqPath([]);
  };

  const handleDeleteFaqItem = (path: string[], index: number) => {
    if (!editedContentData) return;
    const currentArray = getNestedValue(editedContentData.data, path) || [];
    const updatedArray = currentArray.filter((_: any, idx: number) => idx !== index);
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, path, updatedArray),
    });
  };

  // ── Clinic handlers ────────────────────────────────────────────────────────
  const handleAddClinicLocation = (path: string[]) => {
    setClinicPath(path);
    setNewClinicName("");
    setShowAddClinicModal(true);
  };

  const handleSaveClinicLocation = () => {
    if (!editedContentData || !newClinicName.trim()) return;
    const currentArray = getNestedValue(editedContentData.data, clinicPath) || [];
    const updatedArray = [...currentArray, newClinicName.trim()];
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, clinicPath, updatedArray),
    });
    setShowAddClinicModal(false);
    setNewClinicName("");
    setClinicPath([]);
  };

  const handleDeleteClinicLocation = (path: string[], index: number) => {
    if (!editedContentData) return;
    const currentArray = getNestedValue(editedContentData.data, path) || [];
    const updatedArray = currentArray.filter((_: any, idx: number) => idx !== index);
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, path, updatedArray),
    });
  };

  // ── Package card point handlers ────────────────────────────────────────────
  const handleAddPackagePoint = (path: string[]) => {
    setPackagePointPath(path);
    setNewPackagePoint("");
    setShowAddPackagePointModal(true);
  };

  const handleSavePackagePoint = () => {
    if (!editedContentData || !newPackagePoint.trim()) return;
    const currentArray = getNestedValue(editedContentData.data, packagePointPath) || [];
    const updatedArray = [...currentArray, newPackagePoint.trim()];
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, packagePointPath, updatedArray),
    });
    setShowAddPackagePointModal(false);
    setNewPackagePoint("");
    setPackagePointPath([]);
  };

  const handleDeletePackagePoint = (path: string[], index: number) => {
    if (!editedContentData) return;
    const currentArray = getNestedValue(editedContentData.data, path) || [];
    const updatedArray = currentArray.filter((_: any, idx: number) => idx !== index);
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, path, updatedArray),
    });
  };

  // ── Why Choose card handlers ───────────────────────────────────────────────
  const handleAddWhyChooseCard = (path: string[]) => {
    setWhyChooseCardPath(path);
    setNewWhyChooseCard({ heading: "", image: "" });
    setShowAddWhyChooseCardModal(true);
  };

  // Builds the new card from the shape of the existing cards so extra keys
  // (image_alt, description, …) are preserved instead of silently dropped.
  const buildWhyChooseCard = (template: any, heading: string, image: string) => {
    if (!template || typeof template !== "object" || Array.isArray(template)) {
      return { image, heading };
    }
    const card: Record<string, any> = {};
    Object.keys(template).forEach((key) => {
      if (/^(heading|title|text|label)$/i.test(key)) {
        card[key] = heading;
      } else if (/(image|img|icon|photo)/i.test(key) && !/alt/i.test(key)) {
        card[key] = image;
      } else if (typeof template[key] === "object" && template[key] !== null) {
        card[key] = JSON.parse(JSON.stringify(template[key]));
      } else {
        card[key] = "";
      }
    });
    return card;
  };

  const handleSaveWhyChooseCard = () => {
    if (!editedContentData || !newWhyChooseCard.heading.trim()) return;
    const currentArray = getNestedValue(editedContentData.data, whyChooseCardPath) || [];
    const newCard = buildWhyChooseCard(currentArray[0], newWhyChooseCard.heading.trim(), newWhyChooseCard.image.trim());
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, whyChooseCardPath, [...currentArray, newCard]),
    });
    setShowAddWhyChooseCardModal(false);
    setNewWhyChooseCard({ heading: "", image: "" });
    setWhyChooseCardPath([]);
  };

  const handleDeleteWhyChooseCard = (path: string[], index: number) => {
    if (!editedContentData) return;
    const currentArray = getNestedValue(editedContentData.data, path) || [];
    const updatedArray = currentArray.filter((_: any, idx: number) => idx !== index);
    setEditedContentData({
      ...editedContentData,
      data: setNestedValue(editedContentData.data, path, updatedArray),
    });
  };

  // ── Reset helper ───────────────────────────────────────────────────────────
  const resetAll = () => {
    setSuccessMessage(null);
    setContentData(null);
    setPageSlug("");
    setPageType("");
    setCanonicalTag("");
    setResolvedSlug("");
    setCollapsedState({});
    setIsEditing(false);
    setEditedContentData(null);
    setSaveError(null);
    setError(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center pt-6">
      <div className="bg-white rounded-xl shadow p-4 sm:p-8 w-full">
        <h2 className="font-bold mb-2 sm:text-[16px] md:text-2xl">Content Management System</h2>
        <p className="mb-6 text-gray-500">Search for content by page slug and type, then edit and preview changes.</p>

        <div className="space-y-6 mb-6">
          {/* Primary Search Section — slug/city search isn't offered for Doggy World, URL search only */}
          {site !== "doggyworld" && (
            <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <h3 className="text-sm font-semibold text-gray-900">Primary Search</h3>
                <span className="text-xs text-gray-500 ml-auto">Search by Page Slug & City</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Page Slug Field */}
                <div className="flex flex-col">
                  <label className="block font-medium mb-2 text-xs sm:text-sm text-gray-900">
                    Page Slug
                    <span className="text-red-500 ml-1 font-bold">*</span>
                    <span className="text-gray-400 font-normal text-xs ml-2">(Required)</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full rounded-lg px-3 py-2.5 border text-sm placeholder:text-xs sm:placeholder:text-sm transition-colors ${
                      canonicalTag
                        ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-300"
                    } ${isEditing || !!contentData ? "bg-gray-50 border-gray-200" : ""}`}
                    placeholder="e.g., pet-clinic-delhi"
                    value={pageSlug}
                    onChange={(e) => setPageSlug(e.target.value)}
                    disabled={!!canonicalTag}
                    readOnly={isEditing || !!contentData}
                  />
                  <p className="text-xs text-gray-500 mt-1">The unique identifier for your page</p>
                </div>

                {/* City Name Field */}
                <div className="flex flex-col">
                  <label className="block font-medium mb-2 text-xs sm:text-sm text-gray-900">
                    City Name
                    <span className="text-gray-400 font-normal text-xs ml-2">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full rounded-lg px-3 py-2.5 border text-sm placeholder:text-xs sm:placeholder:text-sm transition-colors ${
                      canonicalTag
                        ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-300"
                    } ${isEditing || !!contentData ? "bg-gray-50 border-gray-200" : ""}`}
                    placeholder="e.g., delhi"
                    value={pageType}
                    onChange={(e) => setPageType(e.target.value)}
                    disabled={!!canonicalTag}
                    readOnly={isEditing || !!contentData}
                  />
                  <p className="text-xs text-gray-500 mt-1">Filter results by city (location-specific pages)</p>
                </div>
              </div>
            </div>
          )}

          {/* Alternative Search Section */}
          <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold">
                {site === "doggyworld" ? 1 : 2}
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                {site === "doggyworld" ? "Search by Page URL" : "Alternative Search"}
              </h3>
              {site !== "doggyworld" && (
                <span className="text-xs text-gray-500 ml-auto">Search by Page URL</span>
              )}
            </div>

            <div className="flex flex-col">
              <label className="block font-medium mb-2 text-xs sm:text-sm text-gray-900">
                Page URL
                <span className="text-gray-400 font-normal text-xs ml-2">(Optional)</span>
              </label>
              <input
                type="url"
                className={`w-full rounded-lg px-3 py-2.5 border text-sm placeholder:text-xs sm:placeholder:text-sm transition-colors ${
                  pageSlug || pageType
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-300"
                } ${isEditing || !!contentData ? "bg-gray-50 border-gray-200" : ""}`}
                placeholder={
                  site === "doggyworld"
                    ? "e.g., https://doggyworld.in/ultrasound.php"
                    : "e.g., https://vetic.in/bengaluru/lp/vet-at-home"
                }
                value={canonicalTag}
                onChange={(e) => setCanonicalTag(e.target.value)}
                disabled={pageSlug !== "" || pageType !== ""}
                readOnly={isEditing || !!contentData}
              />
              <p className="text-xs text-gray-500 mt-1">
                {site === "doggyworld"
                  ? "Enter the complete Doggy World page URL"
                  : "Enter the complete page URL (disabled when using Primary Search)"}
              </p>
            </div>
          </div>

          {/* Info Box */}
          {!successMessage && !contentData && !isEditing && (
            <div className="bg-blue-100 border border-blue-300 rounded-xl p-4 flex gap-3">
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-xs sm:text-sm text-blue-900">
                <p className="font-semibold">Search Tips:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5 text-blue-800">
                  {site === "doggyworld" ? (
                    <li>Paste the complete Doggy World page URL to search for it</li>
                  ) : (
                    <>
                      <li>
                        Use <strong>Primary Search</strong> with Page Slug and optionally City name
                      </li>
                      <li>
                        Or use <strong>Alternative Search</strong> with the complete page URL
                      </li>
                      <li>You cannot use both search methods simultaneously</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>

        {!successMessage && !contentData && !isEditing && (
          <button
            onClick={handleSearch}
            disabled={loading || (!pageSlug && !canonicalTag)}
            className={`${PRIMARY_BUTTON} mb-4 disabled:bg-gray-400`}
          >
            {loading ? "Searching..." : "Search Content"}
          </button>
        )}

        {/* ════════════════════════ MODALS ════════════════════════ */}

        {/* ── Image Uploader Modal ── */}
        {imageUploadTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setImageUploadTarget(null);
                setUploadedImageUrl(null);
              }
            }}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
                onClick={() => {
                  setImageUploadTarget(null);
                  setUploadedImageUrl(null);
                }}
              >
                ×
              </button>
              <div className="mb-4 font-bold text-blue-900">Upload Image</div>
              <ImageToUrlGeneration
                onImageUpload={(url: string) => {
                  setUploadedImageUrl(url);
                }}
                resetTrigger={imageUploaderResetCount}
              />
              {uploadedImageUrl && (
                <div className="mt-6">
                  <div className="mb-2 text-green-700 font-semibold">Upload Successful!</div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      className="w-full rounded border border-gray-200 p-2 text-xs bg-gray-50"
                      value={uploadedImageUrl}
                      readOnly
                    />
                    <button
                      className={`${SECONDARY_BUTTON} shrink-0`}
                      onClick={() => navigator.clipboard.writeText(uploadedImageUrl)}
                    >
                      Copy
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className={PRIMARY_BUTTON}
                      onClick={() => {
                        if (imageUploadTarget) {
                          imageUploadTarget.onChange(uploadedImageUrl);
                          setImageUploadTarget(null);
                          setUploadedImageUrl(null);
                          setJustUpdatedFieldPath(imageUploadTarget.path.join("."));
                          setTimeout(() => setJustUpdatedFieldPath(null), 1200);
                        }
                      }}
                    >
                      Update URL
                    </button>
                    <button
                      className={SECONDARY_BUTTON}
                      onClick={() => {
                        setUploadedImageUrl(null);
                        setImageUploaderResetCount((c) => c + 1);
                      }}
                    >
                      Upload Another
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* ── FAQ Add Modal ── */}
        {showAddFaqModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddFaqModal(false);
                setNewFaqItem({ title: "", content: "" });
                setFaqPath([]);
              }
            }}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
                onClick={() => {
                  setShowAddFaqModal(false);
                  setNewFaqItem({ title: "", content: "" });
                  setFaqPath([]);
                }}
              >
                ×
              </button>
              <div className="mb-4 font-bold text-green-900">Add FAQ Item</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    value={newFaqItem.title}
                    onChange={(e) => setNewFaqItem((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter FAQ title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Content</label>
                  <textarea
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    value={newFaqItem.content}
                    onChange={(e) => setNewFaqItem((prev) => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter FAQ content"
                    rows={4}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    className="px-3 py-2 bg-gray-200 text-gray-800 rounded text-sm font-semibold hover:bg-gray-300"
                    onClick={() => {
                      setShowAddFaqModal(false);
                      setNewFaqItem({ title: "", content: "" });
                      setFaqPath([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-3 py-2 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 disabled:bg-green-300"
                    onClick={handleSaveFaqItem}
                    disabled={!newFaqItem.title.trim() || !newFaqItem.content.trim()}
                  >
                    Add FAQ
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Clinic Location Add Modal ── */}
        {showAddClinicModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddClinicModal(false);
                setNewClinicName("");
                setClinicPath([]);
              }
            }}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
                onClick={() => {
                  setShowAddClinicModal(false);
                  setNewClinicName("");
                  setClinicPath([]);
                }}
              >
                ×
              </button>
              <div className="mb-4 font-bold text-blue-900">Add Clinic Location</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Clinic Location Name</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={newClinicName}
                    onChange={(e) => setNewClinicName(e.target.value)}
                    placeholder="Enter clinic location name (e.g., Salt Lake, Park Street)"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    className="px-3 py-2 bg-gray-200 text-gray-800 rounded text-sm font-semibold hover:bg-gray-300"
                    onClick={() => {
                      setShowAddClinicModal(false);
                      setNewClinicName("");
                      setClinicPath([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-3 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700 disabled:bg-blue-300"
                    onClick={handleSaveClinicLocation}
                    disabled={!newClinicName.trim()}
                  >
                    Add Location
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Package Card Point Add Modal ── */}
        {showAddPackagePointModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddPackagePointModal(false);
                setNewPackagePoint("");
                setPackagePointPath([]);
              }
            }}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
                onClick={() => {
                  setShowAddPackagePointModal(false);
                  setNewPackagePoint("");
                  setPackagePointPath([]);
                }}
              >
                ×
              </button>
              <div className="mb-4 font-bold text-green-900">Add Point</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Point</label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    value={newPackagePoint}
                    onChange={(e) => setNewPackagePoint(e.target.value)}
                    placeholder="Enter a package point (e.g., Nail Clipping)"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    className="px-3 py-2 bg-gray-200 text-gray-800 rounded text-sm font-semibold hover:bg-gray-300"
                    onClick={() => {
                      setShowAddPackagePointModal(false);
                      setNewPackagePoint("");
                      setPackagePointPath([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-3 py-2 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 disabled:bg-green-300"
                    onClick={handleSavePackagePoint}
                    disabled={!newPackagePoint.trim()}
                  >
                    Add Point
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Why Choose Card Add Modal ── */}
        {showAddWhyChooseCardModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddWhyChooseCardModal(false);
                setNewWhyChooseCard({ heading: "", image: "" });
                setWhyChooseCardPath([]);
              }
            }}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl"
                onClick={() => {
                  setShowAddWhyChooseCardModal(false);
                  setNewWhyChooseCard({ heading: "", image: "" });
                  setWhyChooseCardPath([]);
                }}
              >
                ×
              </button>
              <div className="mb-4 font-bold text-green-900">Add Card</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Heading
                    <span className="text-red-500 ml-1 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    value={newWhyChooseCard.heading}
                    onChange={(e) => setNewWhyChooseCard((prev) => ({ ...prev, heading: e.target.value }))}
                    placeholder="e.g., For post-op medication"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Image URL
                    <span className="text-gray-400 font-normal text-xs ml-2">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    value={newWhyChooseCard.image}
                    onChange={(e) => setNewWhyChooseCard((prev) => ({ ...prev, image: e.target.value }))}
                    placeholder="https://vetic-img.s3.ap-south-1.amazonaws.com/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to upload it later with the <strong>Image To Url</strong> button on the card.
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    className="px-3 py-2 bg-gray-200 text-gray-800 rounded text-sm font-semibold hover:bg-gray-300"
                    onClick={() => {
                      setShowAddWhyChooseCardModal(false);
                      setNewWhyChooseCard({ heading: "", image: "" });
                      setWhyChooseCardPath([]);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-3 py-2 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 disabled:bg-green-300"
                    onClick={handleSaveWhyChooseCard}
                    disabled={!newWhyChooseCard.heading.trim()}
                  >
                    Add Card
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Schema Add/Edit Modal ── */}
        {showSchemaModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeSchemaModal();
            }}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl relative max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 bg-gray-50 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                onClick={closeSchemaModal}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Header */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="font-bold text-purple-900 text-lg">Schema Data (JSON-LD)</div>

                {schemaValid && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold border border-green-200">
                    <svg className="w-4 h-4" fill="none" strokeWidth={2.5} viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="#22c55e" opacity="0.2" stroke="#22c55e" strokeWidth="2" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                        stroke="#16a34a"
                        strokeWidth="2.5"
                      />
                    </svg>
                    Schema is correct
                  </span>
                )}
                {schemaError && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold border border-red-200">
                    <svg className="w-4 h-4" fill="none" strokeWidth={2} viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="#ef4444" opacity="0.15" stroke="#ef4444" strokeWidth="2" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01"
                        stroke="#dc2626"
                        strokeWidth="2"
                      />
                    </svg>
                    Invalid JSON
                  </span>
                )}
                {!schemaValid && !schemaError && schemaInput.trim() === "" && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-semibold border border-gray-200">
                    Paste JSON to validate
                  </span>
                )}
              </div>

              {/* Textarea */}
              <div className="flex-1 overflow-hidden flex flex-col gap-2">
                <label className="text-sm text-gray-500">
                  Paste your <code className="bg-gray-100 px-1 rounded text-purple-700 text-xs">schemaData</code> JSON
                  below. Validated in real-time.
                </label>
                <textarea
                  className={`w-full rounded-lg shadow-sm p-3 font-mono text-xs resize-none focus:ring-2 transition-all min-h-[320px] border-2 outline-none ${
                    schemaValid
                      ? "border-green-400 focus:ring-green-200"
                      : schemaError
                        ? "border-red-400 focus:ring-red-200"
                        : "border-gray-300 focus:ring-purple-200"
                  }`}
                  value={schemaInput}
                  onChange={(e) => handleSchemaInputChange(e.target.value)}
                  placeholder={`Paste schemaData JSON here...\n\nExample:\n{\n  "@context": "https://schema.org",\n  "@graph": [...]\n}`}
                  spellCheck={false}
                />
                {schemaError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 font-mono">
                    {schemaError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-gray-100">
                <button
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  onClick={closeSchemaModal}
                >
                  Cancel
                </button>
                <button
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-colors ${
                    schemaValid
                      ? "bg-purple-600 hover:bg-purple-700 active:bg-purple-800"
                      : "bg-purple-300 cursor-not-allowed"
                  }`}
                  onClick={handleSaveSchema}
                  disabled={!schemaValid}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Save Schema
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Go Back (edit mode) ── */}
        {isEditing && (
          <button
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold text-sm sm:text-[16px] hover:bg-gray-50 hover:border-gray-400 transition-colors"
            onClick={resetAll}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Go Back
          </button>
        )}

        {error && <div className="mt-4 text-red-500">{error}</div>}

        {successMessage && (
          <div className="mt-6 mb-4 flex flex-col items-center text-center bg-white border border-[#D7F8E4] rounded-2xl shadow-sm px-6 py-10 sm:py-12">
            <div className="w-16 h-16 rounded-full bg-[#ECFDF3] flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-[#17764F]"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">{successMessage}</h3>
            <p className="mt-1.5 text-sm text-gray-500 max-w-sm">
              Your changes have been saved. Please connect with tech team for server deployment to see the change on
              live site.
            </p>
            <button
              className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-[#0467ff] text-white rounded-lg font-semibold text-sm shadow-sm hover:bg-[#0355d6] active:bg-[#0249b8] transition-colors"
              onClick={resetAll}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Go Back
            </button>
          </div>
        )}

        {/* ── Content area ── */}
        {contentData && (
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="sm:text-lg font-semibold">Slug Content</h3>
              <div className="flex gap-2">
                {!isEditing && !successMessage && (
                  <>
                    <button
                      onClick={handleEdit}
                      className="flex items-center gap-1.5 px-5 py-2 sm:py-2.5 bg-[#0467ff] text-white rounded-lg font-semibold text-xs sm:text-sm shadow-sm hover:bg-[#0355d6] active:bg-[#0249b8] transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      className="px-4 py-2 sm:py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold text-xs sm:text-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
                      onClick={resetAll}
                    >
                      Go Back
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="bg-gray-100 rounded p-4 overflow-x-auto mb-4">
              <div className="text-xs text-gray-600 mb-2">
                {canonicalTag ? "Search via: Page URL" : "Search via: Page Slug & City"}
              </div>
              <pre className="break-all">
                <a
                  href={canonicalTag || buildFallbackPageUrl(pageSlug, pageType)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {canonicalTag || buildFallbackPageUrl(pageSlug, pageType)}
                </a>
              </pre>
            </div>

            {isEditing && editedContentData ? (
              <div className="space-y-4">
                {Object.entries(editedContentData).map(([key, value]) => {
                  if (["status", "message", "pageCategory", "page_type", "page_slug"].includes(key)) {
                    return (
                      <div key={key}>
                        <label className="block font-medium mb-1 text-sm capitalize">{key}</label>
                        <input
                          type="text"
                          className="w-full rounded-lg border-gray-200 shadow-sm p-2 bg-gray-100 cursor-not-allowed"
                          value={value === null || value === undefined ? "" : String(value)}
                          readOnly
                        />
                      </div>
                    );
                  }
                  if (key === "data") {
                    return (
                      <div key={key}>
                        <label className="block font-medium mb-1 text-sm capitalize">{key}</label>
                        {renderEditableJson(
                          value,
                          (path, newValue) => {
                            setEditedContentData((prev) => ({
                              ...prev!,
                              data: setNestedValue(prev!.data, path, newValue),
                            }));
                          },
                          [],
                          collapsedState,
                          setCollapsedState,
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={key}>
                      <label className="block font-medium mb-1 text-sm capitalize">{key}</label>
                      <input
                        type="text"
                        className="w-full rounded-lg border-gray-200 shadow-sm p-2 bg-gray-100 cursor-not-allowed"
                        value={value === null || value === undefined ? "" : String(value)}
                        readOnly
                      />
                    </div>
                  );
                })}
                <div className="mt-6 flex items-center gap-3 sticky bottom-0 bg-white/90 backdrop-blur-sm py-3 -mx-1 px-1 border-t border-gray-100">
                  <button
                    onClick={handleSaveChanges}
                    disabled={saveLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#17B26A] text-white rounded-lg font-semibold text-sm shadow-sm hover:bg-[#129359] active:bg-[#0f7a4a] disabled:bg-[#9BE6C1] disabled:cursor-not-allowed transition-colors"
                  >
                    {saveLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold text-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                {saveError && <div className="mt-4 text-red-500">{saveError}</div>}
              </div>
            ) : (
              <iframe
                src={canonicalTag.trim() || buildFallbackPageUrl(pageSlug, pageType)}
                title="Live Page Preview"
                className="w-full h-[90vh] rounded border"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

type TWebsiteChoice = "astro" | "doggyworld";

const VETIC_LOGO_URL =
  "https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/VeticLogoNew.svg";
// Falls back to the Vetic logo (via onError below) if this ever stops resolving.
const DOGGY_WORLD_LOGO_URL = "https://doggyworld.in/assets/images/footer-dw-logo.webp";

const WEBSITE_TILES: {
  id: TWebsiteChoice;
  title: string;
  description: string;
  logoUrl: string;
  // The Vetic SVG logo has a transparent background and needs nothing behind it. The
  // Doggy World logo file has a semi-transparent dark panel baked into its own pixels
  // (it's their site's header/footer logo, not a standalone mark) — it only reads
  // clearly on a dark background matching that panel, so it gets one here, snug and
  // sized to the logo's own 3:1-ish aspect ratio rather than a big empty square.
  logoBg?: string;
}[] = [
  {
    id: "astro",
    title: "Vetic Website",
    description: "Search, edit and update pages on vetic.in.",
    logoUrl: VETIC_LOGO_URL,
  },
  {
    id: "doggyworld",
    title: "Doggy World Website",
    description: "Search, edit and update pages on the Doggy World website.",
    logoUrl: DOGGY_WORLD_LOGO_URL,
    logoBg: "bg-gradient-to-r from-[#d6006e] to-[#ff5b00]",
  },
];

const TileLogo: React.FC<{ logoUrl: string; alt: string; className?: string }> = ({
  logoUrl,
  alt,
  className,
}) => (
  <img
    src={logoUrl}
    alt={alt}
    className={className}
    onError={(event) => {
      if (event.currentTarget.src !== VETIC_LOGO_URL) {
        event.currentTarget.src = VETIC_LOGO_URL;
      }
    }}
  />
);

const ContentManagementSystem: React.FC = () => {
  const [selectedWebsite, setSelectedWebsite] = useState<TWebsiteChoice | null>(null);

  if (!selectedWebsite) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center pt-10">
        <div className="bg-white rounded-xl shadow p-6 sm:p-10 w-full max-w-3xl">
          <h2 className="font-bold mb-2 sm:text-[16px] md:text-2xl">Content Management System</h2>
          <p className="mb-8 text-gray-500">Choose which website you want to edit pages for.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            {WEBSITE_TILES.map((tile) => (
              <button
                key={tile.id}
                type="button"
                onClick={() => setSelectedWebsite(tile.id)}
                className="group text-left border border-[#EAECF0] rounded-xl p-6 sm:p-7 bg-white hover:border-[#0467ff] hover:shadow-md transition-all"
              >
                <div
                  className={`w-32 h-16 flex items-center justify-center mb-5 ${
                    tile.logoBg ? `${tile.logoBg} rounded-lg p-2` : ""
                  }`}
                >
                  <TileLogo
                    logoUrl={tile.logoUrl}
                    alt={`${tile.title} logo`}
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{tile.title}</h3>
                <p className="text-sm text-gray-500 mb-5 leading-relaxed">{tile.description}</p>
                <span className="text-xs font-semibold text-[#0467ff] inline-flex items-center gap-1">
                  Open
                  <HiArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeTile = WEBSITE_TILES.find((tile) => tile.id === selectedWebsite);

  return (
    <div>
      <div className="max-w-5xl mx-auto w-full pt-4 pb-3 px-4 sm:px-0 flex items-center justify-between flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSelectedWebsite(null)}
          className="text-sm text-[#0467ff] hover:underline font-medium inline-flex items-center gap-1"
        >
          ← Back to website selection
        </button>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          Editing:
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 font-semibold text-gray-700">
            <span
              className={`w-9 h-4 flex items-center justify-center ${
                activeTile?.logoBg ? `${activeTile.logoBg} rounded p-0.5` : ""
              }`}
            >
              {activeTile ? (
                <TileLogo
                  logoUrl={activeTile.logoUrl}
                  alt={`${activeTile.title} logo`}
                  className="w-full h-full object-contain"
                />
              ) : null}
            </span>
            {activeTile?.title}
          </span>
        </div>
      </div>
      <SlugManagement site={selectedWebsite === "doggyworld" ? "doggyworld" : undefined} />
    </div>
  );
};

export default ContentManagementSystem;
